<template>
  <div class="practice-report">
    <template v-if="isAnalyzing">
      <div class="practice-report__score">
        <div class="item-status item-status--analyzing">
          <div class="item-status__tag">分析中</div>
        </div>
        <div class="item-info">
          <span class="item-info__name">
            {{ dataInfo.taskName }}
          </span>
          <p class="item-info__time">
            <span>
              <i class="icon-calendar-schedule-line t-iconfont"></i>
              {{ xbb.formatTime(dataInfo.completedTime * 1000, 'yyyy-MM-dd HH:mm') }}
            </span>
            <span class="item-info__cutline"></span>
            <span>
              <i class="icon-time-line t-iconfont"></i>
              {{ dataInfo.duration }}
            </span>
          </p>
        </div>
      </div>
      <div class="cut-line"></div>
      <AnalysisSkeleton :list="analysisSkeletonList" />
      <div class="cut-line"></div>
      <AnalysisSkeleton :list="sessionSkeletonList" secondary />
    </template>
    <template v-else>
      <div class="practice-report__score">
        <div class="item-status">
          <span class="item-status__score" :style="!dataInfo.score && 'color: #F54E4E'">
            {{ dataInfo.score || 0 }}
          </span>
          <span class="item-status__normal">/100</span>
        </div>
        <div class="item-info">
          <span class="item-info__name">
            {{ dataInfo.taskName }}
          </span>
          <p class="item-info__time">
            <span>
              <i class="icon-calendar-schedule-line t-iconfont"></i>
              {{ xbb.formatTime(dataInfo.completedTime * 1000, 'yyyy-MM-dd HH:mm') }}
            </span>
            <span class="item-info__cutline"></span>
            <span>
              <i class="icon-time-line t-iconfont"></i>
              {{ dataInfo.duration }}
            </span>
          </p>
        </div>
      </div>
      <div class="cut-line"></div>
      <div class="section-tabs">
        <button
          class="section-tabs__item"
          :class="{ 'section-tabs__item--active': activeTab === 'analysis' }"
          type="button"
          @click="activeTab = 'analysis'"
        >
          亮点与不足
        </button>
        <button
          class="section-tabs__item"
          :class="{ 'section-tabs__item--active': activeTab === 'session' }"
          type="button"
          @click="activeTab = 'session'"
        >
          对话原文
        </button>
      </div>
      <template v-if="activeTab === 'analysis'">
        <div v-if="dataInfo.messageList?.length" class="record-analysis">
          <div v-if="dataInfo.practiceResult?.good?.length" class="record-analysis__good">
            <AnalysisCard
              v-for="(good, index) in dataInfo.practiceResult.good"
              :key="index"
              :data-info="{
                ...good,
                status: 'good'
              }"
              :message-list="dataInfo.messageList"
              @view-quote="handleViewQuote"
            />
          </div>
          <div v-if="dataInfo.practiceResult?.bad?.length" class="record-analysis__bad">
            <AnalysisCard
              v-for="(bad, index) in dataInfo.practiceResult.bad"
              :key="index"
              :data-info="{
                ...bad,
                status: 'bad'
              }"
              :message-list="dataInfo.messageList"
              @view-quote="handleViewQuote"
            />
          </div>
        </div>
        <div v-else class="analysis-empty">
          <img class="analysis-empty__img" :src="empty" />
          <div class="analysis-empty__desc">
            本次练习未检测到有效发言，暂不产生评分（记为0分）放轻松，整理下思路，下次大胆开口试一试吧！
          </div>
        </div>
      </template>
      <template v-else>
        <div v-if="dataInfo.messageList?.length" class="record-session">
          <SessionCard
            ref="sessionCardRef"
            :bad-list="dataInfo.practiceResult?.bad || []"
            :data-info="dataInfo"
            :data-list="dataInfo.messageList"
          />
        </div>
        <div v-else class="analysis-empty">
          <img class="analysis-empty__img" :src="empty" />
          <div class="analysis-empty__desc">本次练习未检测到有效发言</div>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup>
import empty from '@/assets/empty-bg.png'
import { computed, onMounted, onUnmounted, ref, defineProps, nextTick } from 'vue'
// api
import { getTrainResult } from '@/api/chat.js'
// utils
import xbb from '@xbb/xbb-utils'
// props
const props = defineProps({
  params: {
    type: Object,
    default: () => ({})
  }
})
// components
import AnalysisCard from './analysis-card.vue'
import AnalysisSkeleton from './analysis-skeleton.vue'
import SessionCard from './session-card.vue'
// data
const pollTimer = ref(null)
const isPolling = ref(false)
const activeTab = ref('analysis')
const sessionCardRef = ref(null)
const dataInfo = ref({
  trainRecordId: 0,
  taskName: '',
  duration: '',
  status: 0,
  score: 0,
  completedTime: 0,
  customerProfile: '',
  customerQuote: '',
  messageList: [],
  practiceResult: null,
  userName: ''
})
const isAnalyzing = computed(() => !dataInfo.value.practiceResult)
const analysisSkeletonList = ['short', 'full', 'full', 'full', 'full', 'medium']
const sessionSkeletonList = ['short', 'full', 'full', 'full', 'medium']

// methods
const getDataDetail = () => {
  if (isPolling.value) return
  isPolling.value = true

  getTrainResult({
    ...props.params
  })
    .then((res) => {
      dataInfo.value = res.result
      if (res.result?.practiceResult) {
        stopPolling()
      }
    })
    .finally(() => {
      isPolling.value = false
    })
}

const startPolling = () => {
  if (pollTimer.value) return
  pollTimer.value = setInterval(() => {
    getDataDetail()
  }, 10000)
}

const stopPolling = () => {
  if (!pollTimer.value) return
  clearInterval(pollTimer.value)
  pollTimer.value = null
}

const handleViewQuote = (messageIndex) => {
  activeTab.value = 'session'
  nextTick(() => {
    sessionCardRef.value?.scrollToMessage?.(messageIndex)
  })
}

// lice-cycle
onMounted(() => {
  getDataDetail()
  startPolling()
})

onUnmounted(() => {
  stopPolling()
})
</script>

<style lang="less" scoped>
.practice-report {
  position: relative;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 12px;

  .cut-line {
    margin: 16px 0;
    // border-top: 1px dashed #f2f3f5;
    height: 1px;
    background: repeating-linear-gradient(
      to right,
      #f2f3f5,
      #f2f3f5 4px,
      transparent 4px,
      transparent 8px
    );
  }

  .title-line {
    line-height: 26px;
    color: #323233;
    font-size: 18px;
    font-weight: 600;
  }

  .section-tabs {
    display: flex;
    align-items: center;
    gap: 12px;

    &__item {
      min-width: 68px;
      height: 24px;
      padding: 4px 10px;
      border: none;
      border-radius: 8px;
      background: #f2f3f5;
      color: #323233;
      font-size: 12px;
      line-height: 16px;
      font-family: inherit;
      cursor: pointer;
    }

    &__item + &__item {
      margin-left: 12px;
    }

    &__item--active {
      background: #7a52f4;
      color: #fff;
    }
  }

  &__score {
    display: flex;
    align-items: center;
    padding: 12px;
    background: linear-gradient(283deg, #eef2ff 1%, #f5f3ff 100%);
    border-radius: 16px;
    border: 1px solid rgba(124, 58, 237, 0.15);

    .item-status {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 66px;
      height: 66px;
      border-radius: 12px;
      background: #fff;

      &__score {
        line-height: 34px;
        font-weight: 600;
        font-size: 24px;
        color: #7a52f4;
      }

      &__normal {
        line-height: 16px;
        font-size: 12px;
        color: #969799;
      }

      &__tag {
        padding: 2px 8px;
        border-radius: 16px;
        background: #f2f3f5;
        line-height: 16px;
        font-size: 12px;
        color: #646566;
      }
    }

    .item-status--analyzing {
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.04);
    }

    .item-info {
      margin-left: 12px;
      display: flex;
      flex-direction: column;

      &__name {
        font-size: 16px;
        font-weight: 600;
        line-height: 22px;
        color: #323233;

        .t-iconfont {
          color: #969799;
        }
      }

      &__cutline {
        width: 1px;
        height: 12px;
        background-color: #e5e6eb;
      }

      &__time {
        min-height: 16px;
        display: flex;
        align-items: center;
        margin-top: 4px;
        font-size: 12px;
        color: #323233;

        > span + span {
          margin-left: 12px;
        }

        .t-iconfont {
          margin-right: 2px;
          font-size: 12px;
          width: 12px;
          height: 12px;
          color: #969799;
        }
      }
    }
  }

  .record-analysis {
    display: flex;
    flex-direction: column;
    margin-top: 16px;

    &__good,
    &__bad {
      box-sizing: border-box;
      padding: 12px;
      display: flex;
      flex-direction: column;
      border-radius: 16px;
      border-left: 2px solid transparent;

      > * + * {
        position: relative;
        margin-top: 12px;
        padding-top: 12px;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: repeating-linear-gradient(
            to right,
            #e5e6eb,
            #e5e6eb 4px,
            transparent 4px,
            transparent 8px
          );
        }
      }
    }

    &__bad {
      margin-top: 12px;
    }

    &__good {
      background: #f7f8fa;
      border-left-color: #00c241;
    }

    &__bad {
      background: #f7f8fa;
      border-left-color: #f54e4e;
    }
  }

  .record-session {
    margin-top: 16px;
  }

  .analysis-empty {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    &__img {
      margin-top: 12px;
    }
    &__desc {
      color: #969799;
      font-size: 14px;
      line-height: 20px;
      text-align: center;
    }
  }
}
</style>
